import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProblemMap } from './validation.js';

/**
 * The Designer's validation messages, delivered to the controls that own them.
 *
 * 🚨 A context rather than props, deliberately. The controls that need an `error` are spread over
 * `tabs.tsx`, `BlockControls.tsx`, `CustomFieldControls.tsx` and `FieldEditor.tsx` — around forty
 * of them, most nested two or three components below the tab. Threading a `problems` prop through
 * every one of those signatures would touch far more code than the feature is worth, and every
 * component that forgot to forward it would silently lose its errors.
 *
 * The map is derived from the draft on every render (`configProblems`), so a message cannot
 * outlive the value it describes — the same "derived, not stored" property `validateThankYou`
 * relies on in Settings.
 */

const ProblemsContext = createContext<ProblemMap | null>(null);

export function DesignerProblems({
  problems,
  children,
}: {
  problems: ProblemMap;
  children: ReactNode;
}): JSX.Element {
  return <ProblemsContext.Provider value={problems}>{children}</ProblemsContext.Provider>;
}

/**
 * Look up a control's validation message by its config path (`messages.required`), or by the
 * field-key alias for per-field controls (`fieldProblemPath(key, 'label')`).
 *
 * Returns `undefined` when the control is fine — which is exactly what `TextControl`'s `error`
 * prop expects, so call sites read `error={problem('messages.required')}` and nothing else.
 *
 * Outside a provider (the field cards are also rendered from places that do not validate) every
 * lookup is `undefined`, so controls degrade to their previous behaviour rather than crashing.
 */
export function useFieldProblem(): (path: string) => string | undefined {
  const problems = useContext(ProblemsContext);
  const { t } = useTranslation();
  return useMemo(
    () =>
      (path: string): string | undefined => {
        const problem = problems?.get(path);
        if (!problem) return undefined;
        return t(`designer.validation.${problem.key}`, problem.params ?? {});
      },
    [problems, t],
  );
}
